import type { TrialGenerationStep, TrialModuleKey } from '../types';
import { TRIAL_MODULE_KEYS } from '../types';
import { TRIAL_MODULE_ICON_PATHS } from '../containers/free-trial/trial-icons';

const STEP_ICONS: Record<string, string> = {
  'research.await': 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2V5zM4 19a2 2 0 012-2h13',
  'content.bundle': 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2V5zM4 19a2 2 0 012-2h13',
  branding: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
  structure: 'M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c1-4 4-6 7-6s6 2 7 6M17 11a3 3 0 100-6M22 21c-.5-3-2.5-5-5-5.5',
  users: 'M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c1-4 4-6 7-6s6 2 7 6M17 11a3 3 0 100-6M22 21c-.5-3-2.5-5-5-5.5',
  channels: 'M4 5h16v11H9l-5 4V5z',
  values: 'M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1L3.2 9.5l6.1-.9z',
  forms: 'M9 3h6v3H9zM7 4.5H5V21h14V4.5h-2M8 11h8M8 15h5',
  'documents.initial': 'M4 5a2 2 0 012-2h13v16H6a2 2 0 00-2 2V5zM4 19a2 2 0 012-2h13',
  events: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  'communication.initial': 'M3 11v2a1 1 0 001 1h2l5 4V6L6 10H4a1 1 0 00-1 1zM15 9a3 3 0 010 6M18 6a7 7 0 010 12',
  'ai-buddy': 'M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2z',
  shortcuts: 'M9 6h11M9 12h11M9 18h11M4 6l1 1 2-2M4 12l1 1 2-2M4 18l1 1 2-2',
  'gate.complete': 'M3 17l6-6 4 4 8-8M15 7h6v6',
  tasks: 'M9 3h6v3H9zM7 4.5H5V21h14V4.5h-2M8 11h8M8 15h5',
  performance: 'M3 17l6-6 4 4 8-8M15 7h6v6',
  onboarding: 'M5 21V4M5 4h11l-2 4 2 4H5',
};

export const TRIAL_MODULE_STEP_KEYS: Record<TrialModuleKey, string[]> = {
  news: ['communication.initial'],
  chat: ['channels'],
  directory: ['users', 'structure'],
  events: ['events'],
  kb: ['documents.initial'],
  ai: ['ai-buddy'],
  forms: ['forms'],
  tasks: ['tasks'],
  onboarding: ['onboarding', 'values'],
  elearning: ['content.bundle', 'academy'],
  performance: ['performance'],
  analytics: ['usage-history'],
  surveys: ['checkin-surveys', 'pulse-survey'],
  recognition: ['recognition'],
};

const FALLBACK_ICON = STEP_ICONS['gate.complete'];

export type BuildModule = {
  key: string;
  label: string;
  d: string;
  progress: number;
  done: boolean;
  building: boolean;
  failed: boolean;
  statusText: string | null;
  collapseDelay: string;
};

function clampPercent(value: number) {
  return Math.min(100, Math.max(0, Math.round(value)));
}

function isStepFinished(step: TrialGenerationStep) {
  return step.status === 'done' || step.status === 'skipped' || step.status === 'failed';
}

function stepProgress(step: TrialGenerationStep) {
  if (isStepFinished(step)) return 100;
  if (typeof step.percent === 'number') return clampPercent(step.percent);

  return step.status === 'running' ? 55 : 0;
}

function gateSharePercent(steps: TrialGenerationStep[]) {
  const gate = steps.filter((step) => step.phase === 'gate');

  if (!gate.length) return null;

  const weights = gate.map((step) => (
    typeof step.weight_percent === 'number' && step.weight_percent > 0
      ? step.weight_percent
      : 1
  ));
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const earned = gate.reduce((sum, step, index) => (
    sum + weights[index] * (stepProgress(step) / 100)
  ), 0);

  return clampPercent((earned / total) * 100);
}

export function displayPercent(
  percent: number | undefined,
  isReady: boolean,
  steps: TrialGenerationStep[] = [],
  hasGeneration = false,
) {
  if (isReady) return 100;

  if (typeof percent === 'number' && Number.isFinite(percent)) {
    const fromApi = clampPercent(percent);
    const capped = Math.min(99, fromApi);

    if (hasGeneration && capped === 0) return 1;

    return capped;
  }

  const fromGate = gateSharePercent(steps);

  const raw = fromGate != null
    ? Math.min(99, fromGate)
    : Math.min(99, Math.max(0, Math.round((percent ?? 0) * 2)));

  if (hasGeneration && raw === 0) return 1;

  return raw;
}

export function buildStatusLabel(
  steps: TrialGenerationStep[] = [],
  modules: BuildModule[] = [],
) {
  const running = steps.find((step) => step.status === 'running');

  if (running?.label) return running.label;

  return modules.find((mod) => mod.building)?.label
    ?? modules.find((mod) => !mod.done && !mod.failed)?.label
    ?? null;
}

function aggregateModuleStep(steps: TrialGenerationStep[], keys: string[]) {
  const related = steps.filter((step) => keys.includes(step.key));

  if (!related.length) {
    return {
      progress: 0,
      done: false,
      building: false,
      failed: false,
      statusText: null as string | null,
    };
  }

  const failed = related.find((step) => step.status === 'failed');
  const building = related.find((step) => step.status === 'running');
  const done = related.every((step) => isStepFinished(step));
  const progress = Math.max(...related.map(stepProgress));

  return {
    progress,
    done,
    building: !!building,
    failed: !!failed,
    statusText: failed
      ? failed.error || failed.message
      : building
        ? building.message
        : null,
  };
}

export function toSelectedBuildModules(
  steps: TrialGenerationStep[] = [],
  selectedModules: TrialModuleKey[] = [],
  labelFor: (key: TrialModuleKey) => string,
): BuildModule[] {
  const modules = selectedModules.length > 0 ? selectedModules : [...TRIAL_MODULE_KEYS];

  return modules.map((moduleKey, index) => {
    const keys = TRIAL_MODULE_STEP_KEYS[moduleKey] ?? [moduleKey];
    const state = aggregateModuleStep(steps, keys);

    return {
      key: moduleKey,
      label: labelFor(moduleKey),
      d: TRIAL_MODULE_ICON_PATHS[moduleKey] ?? STEP_ICONS[moduleKey] ?? FALLBACK_ICON,
      progress: state.progress,
      done: state.done,
      building: state.building,
      failed: state.failed,
      statusText: state.statusText,
      collapseDelay: `${0.15 + (index % 8) * 0.06}s`,
    };
  });
}

/** While gate runs, keep selected module bars in sync with overall API progress. */
export function withSharedGateProgress(
  modules: BuildModule[],
  apiPercent: number | undefined,
  steps: TrialGenerationStep[] = [],
): BuildModule[] {
  const shared = displayPercent(apiPercent, false, steps, steps.length > 0);

  if (shared <= 0) return modules;

  return modules.map((mod) => {
    if (mod.done || mod.failed) return mod;

    const next = Math.max(mod.progress, shared);

    if (next === mod.progress) return mod;

    return { ...mod, progress: next };
  });
}

export function toGateModules(steps: TrialGenerationStep[] = []): BuildModule[] {
  return steps
    .filter((step) => step.phase === 'gate')
    .map((step, index) => {
      const done = step.status === 'done' || step.status === 'skipped';
      const building = step.status === 'running';
      const failed = step.status === 'failed';

      return {
        key: step.key,
        label: step.label,
        d: STEP_ICONS[step.key] ?? FALLBACK_ICON,
        progress: stepProgress(step),
        done,
        building,
        failed,
        statusText: failed ? step.error || step.message : building ? step.message : null,
        collapseDelay: `${0.15 + (index % 8) * 0.06}s`,
      };
    });
}
